import { z } from 'zod';

import {
  AllTestStatuses,
  ResourceType,
  ResourceTypeExplorer,
  ResourceTypeOrUnknown,
  resourceTypes,
  resourceTypesExplorer,
} from '@dbt-labs/dbt-dag';

import { ResourceNodeType, TestStatus, TestType } from '@/__generated__/graphql';
import { RESOURCE_TYPE_MAPPING } from '@/typings/search';
import { buildTypePredicate } from './type';

export {
  type ResourceType,
  type ResourceTypeExplorer,
  type ResourceTypeOrUnknown,
  resourceTypes,
  resourceTypesExplorer,
};

export const capitalizedResourceNames: Record<ResourceTypeExplorer, string> = {
  analysis: 'Analysis',
  column: 'Column',
  exposure: 'Exposure',
  function: 'Function',
  group: 'Group',
  macro: 'Macro',
  metric: 'Metric',
  model: 'Model',
  project: 'Project',
  saved_query: 'Saved Query',
  seed: 'Seed',
  semantic_model: 'Semantic Model',
  snapshot: 'Snapshot',
  source: 'Source',
  test: 'Test',
  unit_test: 'Unit Test',
};

export function toSearchResourceType(
  input: ResourceTypeExplorer,
): ResourceNodeType | null {
  const mappedResourceType = RESOURCE_TYPE_MAPPING[input];
  if (!mappedResourceType) return null;
  return mappedResourceType;
}

type ResourceTypeResult =
  | {
      success: ResourceNodeType[] | undefined;
      error?: never;
    }
  | {
      success?: never;
      error: string;
    };

export function toSearchResourceTypes(
  input: string[] | undefined | null,
): ResourceTypeResult {
  if (!input) return { success: [] };
  const results = { success: [] as ResourceNodeType[] };
  for (const resourceType of input) {
    const result = toSearchResourceType(resourceType as ResourceTypeExplorer);
    if (result) {
      results.success.push(result);
      continue;
    }
    if (['default', 'all'].includes(resourceType)) {
      return { success: undefined };
    }
    return { error: `Invalid resource type: \`${resourceType}\`` };
  }
  return results;
}

export const pluralizedCapitalizedResourceNames: Record<ResourceTypeExplorer, string> =
  {
    analysis: 'Analyses',
    column: 'Columns',
    exposure: 'Exposures',
    function: 'Functions',
    group: 'Groups',
    macro: 'Macros',
    metric: 'Metrics',
    model: 'Models',
    project: 'Projects',
    saved_query: 'Saved Queries',
    seed: 'Seeds',
    semantic_model: 'Semantic Models',
    snapshot: 'Snapshots',
    source: 'Sources',
    test: 'Tests',
    unit_test: 'Unit Tests',
  };

export const pluralizedResourceNames: Record<ResourceTypeExplorer, string> = {
  analysis: 'analyses',
  column: 'columns',
  exposure: 'exposures',
  function: 'functions',
  group: 'groups',
  macro: 'macros',
  metric: 'metrics',
  model: 'models',
  project: 'projects',
  saved_query: 'saved queries',
  seed: 'seeds',
  semantic_model: 'semantic models',
  snapshot: 'snapshots',
  source: 'sources',
  test: 'tests',
  unit_test: 'unit tests',
};

export const pluralizeResourceType = (
  resourceType: ResourceTypeExplorer,
  isCapitalized = false,
) => {
  if (isCapitalized) {
    return pluralizedCapitalizedResourceNames[resourceType];
  }
  return pluralizedResourceNames[resourceType];
};

export const isResourceType = buildTypePredicate(resourceTypesExplorer);

export const zResourceTypeExplorer = z.string().transform((input, ctx) => {
  if (isResourceType(input)) {
    return input as ResourceTypeExplorer;
  }
  const lc = input.toLowerCase().replaceAll('_', '');
  const resourceMatch = resourceTypes.find((resource) =>
    lc.startsWith(resource.replaceAll('_', '')),
  );
  if (resourceMatch) {
    return resourceMatch;
  }
  ctx.addIssue({
    code: z.ZodIssueCode.custom,
    message: `Is not valid resource type, expected: ${resourceTypesExplorer}`,
  });
  return z.NEVER;
});

export function validateResourceType(resourceType?: string | null) {
  const parseResult = zResourceTypeExplorer.safeParse(resourceType);
  if (parseResult.success) {
    return parseResult.data;
  }
  return undefined;
}

export function getResourceTypeName(resourceType: string | undefined | null): string {
  const validatedType = validateResourceType(resourceType);
  if (!validatedType) return 'Resources';

  return pluralizeResourceType(validatedType, true);
}

export function translateToTestStatus(
  testStatus: AllTestStatuses | undefined,
): TestStatus | null | undefined {
  if (testStatus === undefined) return undefined;
  if (!testStatus) return null;
  switch (testStatus) {
    case 'unknown':
      return null;
    case 'pass':
      return TestStatus.Pass;
    case 'error':
      return TestStatus.Error;
    case 'fail':
      return TestStatus.Fail;
    case 'warn':
      return TestStatus.Warn;
    case 'skipped':
      return TestStatus.Skipped;
    default:
      return null;
  }
}

export function translateFromTestType(testType: TestType | null): 'unit' | 'data' {
  switch (testType) {
    case TestType.UnitTest:
      return 'unit';
    default:
      return 'data';
  }
}

export function translateToTestType(
  testTypeDisplayString: 'unit' | 'data',
): TestType[] {
  switch (testTypeDisplayString) {
    case 'unit':
      return [TestType.UnitTest];
    default:
      return [TestType.GenericDataTest, TestType.SingularDataTest];
  }
}
